// Data model types for the frontend, backed by the `/contacts`,
// `/accounts`, and nested `/activities` REST endpoints.
//
// Lifecycle vocabulary (post-LT-358, 11 stages) — Functional Architecture §5:
// Pass 0 baseline (LT-358 renamed `new` → `cold`):
//   cold | contacted | qualified | disqualified | customer
// Pass 1 (LT-328):
//   in_sequence | callback_scheduled | opportunity_open | nurture
// Pass 2 (LT-358):
//   engaged | no_answer | dormant
//
// `callback_scheduled` is load-bearing for the freestanding callback
// subsystem (LT-330). `dormant` is driven by a daily backend sweeper
// (90-day inactivity threshold, configurable).
export type LifecycleStage =
  | 'cold'
  | 'contacted'
  | 'qualified'
  | 'disqualified'
  | 'customer'
  | 'in_sequence'
  | 'callback_scheduled'
  | 'opportunity_open'
  | 'nurture'
  | 'engaged'
  | 'no_answer'
  | 'dormant';

export const LIFECYCLE_STAGES: readonly LifecycleStage[] = [
  'cold',
  'contacted',
  'qualified',
  'disqualified',
  'customer',
  'in_sequence',
  'callback_scheduled',
  'opportunity_open',
  'nurture',
  'engaged',
  'no_answer',
  'dormant',
] as const;

// Human-readable labels for filter dropdowns, bulk-edit pickers, and
// lifecycle badges. Keep in lockstep with `LifecycleStage`.
export const LIFECYCLE_STAGE_LABELS: Record<LifecycleStage, string> = {
  cold: 'Cold',
  contacted: 'Contacted',
  qualified: 'Qualified',
  disqualified: 'Disqualified',
  customer: 'Customer',
  in_sequence: 'In sequence',
  callback_scheduled: 'Callback scheduled',
  opportunity_open: 'Opportunity open',
  nurture: 'Nurture',
  engaged: 'Engaged',
  no_answer: 'No answer',
  dormant: 'Dormant',
};

export type ActivityType = 'call' | 'email' | 'sms' | 'note' | 'meeting';
export type ActivityDirection = 'inbound' | 'outbound' | 'internal';

export interface Contact {
  id: string;
  workspace_id: string;
  account_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  lifecycle_stage: LifecycleStage;
  engagement_score?: number | null;
  temperature?: string | null;
  // Live post-call classifications written by the OpenAI analyzer; user-
  // overridable via the Contact Detail "Live classification" panel (LT-212).
  current_temperature?: string | null;
  current_disposition?: string | null;
  owner_id?: string | null;
  source?: string | null;
  lead_provided_info?: Record<string, unknown> | null;
  last_activity_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  // LT-393 — Contact cockpit L4 override metadata. Non-null when the
  // operator has overridden the §9.6 prior-call summary; the cockpit's
  // right-rail drawer surfaces this so the next dispatch matches.
  prior_call_summary_override?: string | null;
  prior_call_summary_overridden_at?: string | null;
}

export interface ContactCreate {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  title?: string;
  account_id?: string;
  owner_id?: string;
  source?: string;
  lifecycle_stage?: LifecycleStage;
  engagement_score?: number;
  temperature?: string;
}

// Update accepts everything Create does PLUS the two live-classification
// fields (LT-211/212) that Create does NOT accept (server-side create has
// a fixed signature and these are populated post-call, not at insert).
// Identity fields accept `null` so the cockpit edit-details modal (LT-471)
// can clear a previously-set email / phone / title — the backend
// ContactUpdate schema treats `null` as "clear this column".
export type ContactUpdate = Partial<
  Omit<ContactCreate, 'first_name' | 'last_name' | 'email' | 'phone' | 'title'>
> & {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  current_temperature?: string | null;
  current_disposition?: string | null;
};

// LT-360 — Append-only audit row per contact.lifecycle_stage transition.
// Surfaces on the contact-detail timeline panel.
export type LifecycleHistoryTrigger = 'manual' | 'sequence' | 'api' | 'backfill';

export interface ContactLifecycleHistoryEntry {
  id: string;
  contact_id: string;
  workspace_id: string;
  from_stage: string | null;
  to_stage: string;
  transitioned_at: string;
  trigger: LifecycleHistoryTrigger;
  triggered_by_user_id?: string | null;
  triggered_by_sequence_step_id?: string | null;
  reason?: string | null;
}

export interface ContactListFilters {
  lifecycle_stage?: LifecycleStage;
  owner_id?: string;
  account_id?: string;
  since?: string;
  until?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface Account {
  id: string;
  workspace_id: string;
  name: string;
  domain?: string | null;
  industry?: string | null;
  owner_id?: string | null;
  source?: string | null;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
}

export interface AccountCreate {
  name: string;
  domain?: string;
  industry?: string;
  owner_id?: string;
  source?: string;
}

export type AccountUpdate = Partial<AccountCreate>;

export interface Activity {
  id: string;
  workspace_id: string;
  contact_id: string;
  account_id?: string | null;
  type: ActivityType;
  direction: ActivityDirection;
  actor_user_id?: string | null;
  occurred_at: string;
  subject?: string | null;
  summary?: string | null;
  call_summary_id?: string | null;
  email_log_id?: string | null;
  sms_log_id?: string | null;
  meeting_id?: string | null;
  body?: string | null;
  activity_metadata?: Record<string, unknown> | null;
  created_at: string;
}

// LT-167 — engagement sub-events surfaced under each email Activity row in
// the Contact detail timeline. Fetched lazily per-row so the main timeline
// query stays thin.
export type EmailEventType =
  | 'delivered'
  | 'open'
  | 'click'
  | 'bounce'
  | 'dropped'
  | 'spamreport'
  | 'unsubscribe';

export interface EmailEvent {
  event_type: EmailEventType | string;
  occurred_at: string;
  bounce_reason?: string | null;
  url?: string | null;
  user_agent?: string | null;
  ip_address?: string | null;
}

export interface NoteCreate {
  body: string;
  subject?: string;
}

export interface NoteUpdate {
  body?: string;
  subject?: string;
}

export interface BulkUploadResponse {
  created: number;
  errors: string[];
  // LT-657 — set when the upload dropped the batch into a static contact
  // list (new or existing). `created_contact_ids` is always returned so
  // the caller can act on the fresh batch (e.g. auto-select / add-to-list).
  list_id?: string | null;
  created_contact_ids?: string[];
}

// LT-330 — freestanding callback subsystem.
export type ScheduledCallbackStatus =
  | 'pending'
  | 'fired'
  | 'cancelled'
  | 'failed_to_fire';

export interface ScheduledCallback {
  id: string;
  workspace_id: string;
  contact_id: string;
  scheduled_at: string;
  agent_id_snapshot?: string | null;
  sequence_id_snapshot?: string | null;
  campaign_id_snapshot?: string | null;
  notes?: string | null;
  status: ScheduledCallbackStatus;
  created_at: string;
  fired_at?: string | null;
  cancelled_at?: string | null;
  cancelled_by_user_id?: string | null;
  cancelled_reason?: string | null;
  fired_call_session_id?: string | null;
}

export interface ScheduledCallbackCreatePayload {
  scheduled_at: string;
  agent_id_snapshot?: string | null;
  sequence_id_snapshot?: string | null;
  campaign_id_snapshot?: string | null;
  context_snapshot?: Record<string, unknown> | null;
  notes?: string | null;
}
